import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import Select from "react-select";
import { OptionValueType } from "appz";
import { PersonalDatePresentationItem } from "burde";
import { useResources } from "contexts/resource-context";
import { actionCreators, updatePersonalDateInfo } from "page/calendar/store";
import {
    actionCreators as userActionCreators,
    getIsAuthenticated,
} from "user/store";
import { actionCreators as uiActionCreators } from "ui/store";
import { ApplicationState } from "store";
import { getDatesets, saveDateset } from "calendar/service";
import { RecurringYear, removeEmojis } from "ui/constants";
import ControlsInfoBox from "../controls-infobox";
import Button from "content/button/button";
import * as styles from "./dateset-picker.css";

type Props = {
    isAuthenticated: boolean;
    updatePersonalDateInfo: (
        personalDateItems: PersonalDatePresentationItem[],
    ) => any;
} & typeof actionCreators &
    typeof uiActionCreators &
    typeof userActionCreators;

type OptionType = OptionValueType<string, number>;

const selectableIcons = [
    "fa-birthday-cake",
    "fa-heart",
    "fa-plane",
    "fa-smile",
    "fa-frown",
];

const DatesetPicker = ({
    isAuthenticated,
    updateShowLoginModal,
    updatePersonalDateInfo,
}: Props) => {
    const [dateset, setDateset] = useState<
        PersonalDatePresentationItem[] | null
    >(null);
    const [selectedMonthNumber, setSelectedMonthNumber] = useState<
        number | null
    >(null);
    const [selectedDayNumber, setSelectedDayNumber] = useState<number | null>(
        null,
    );
    const [personalDateText, setPersonalDateText] = useState<string>("");
    const [personalDateIcon, setPersonalDateIcon] = useState<string>("");
    const [errorText, setErrorText] = useState<string>("");

    const resources = useResources();

    useEffect(() => {
        if (isAuthenticated) {
            getDatesets().then((response) => {
                setDateset(response);
            });
        } else {
            setDateset(null);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        updatePersonalDateInfo(dateset !== null ? dateset : []);
    }, [dateset]);

    const getMonthOptions = (): OptionType[] => {
        return Array.from({ length: 12 }, (_e, i) => ({
            value: i + 1,
            label: moment().month(i).format("MMMM"),
        }));
    };

    const getDayOptions = (monthNumber: number | null): OptionType[] => {
        const length = moment()
            .year(RecurringYear)
            .month(monthNumber === null ? 0 : monthNumber - 1)
            .daysInMonth();

        return Array.from({
            length,
        }).map((_e, i) => {
            const value = i + 1;

            return {
                value,
                label: value >= 10 ? value.toString() : `0${value}`,
            };
        });
    };

    const validateFields = () => {
        if (personalDateText === "") {
            setErrorText(resources["CustomDates_Error_MissingDescription"]);
            return false;
        }

        if (personalDateIcon === "") {
            setErrorText(resources["CustomDates_Error_MissingIcon"]);
            return false;
        }

        if (selectedMonthNumber === null) {
            setErrorText(resources["CustomDates_Error_MissingMonth"]);
            return false;
        }

        if (selectedDayNumber === null) {
            setErrorText(resources["CustomDates_Error_MissingDay"]);
            return false;
        }

        // If one tries appending a personal date to a day where one already has been applied
        if (dateset !== null) {
            const dayString =
                selectedDayNumber < 10
                    ? `0${selectedDayNumber}`
                    : `${selectedDayNumber}`;
            const monthString =
                selectedMonthNumber < 10
                    ? `0${selectedMonthNumber}`
                    : `${selectedMonthNumber}`;
            const dateString = `${monthString}-${dayString}`;
            const existingDate = dateset.find((x) => x.date === dateString);

            if (existingDate) {
                setErrorText(resources["CustomDates_Error_DuplicateDate"]);
                return false;
            }
        }

        setErrorText("");
        return true;
    };

    const selectedMonth =
        getMonthOptions().find((x) => x.value === selectedMonthNumber) || null;
    const selectedDay =
        getDayOptions(selectedMonthNumber).find(
            (x) => x.value === selectedDayNumber,
        ) || null;

    const savePersonalDate = () => {
        if (
            !validateFields() ||
            selectedDayNumber === null ||
            selectedMonthNumber === null
        ) {
            return;
        }

        const dayString =
            selectedDayNumber < 10
                ? `0${selectedDayNumber}`
                : `${selectedDayNumber}`;
        const monthString =
            selectedMonthNumber < 10
                ? `0${selectedMonthNumber}`
                : `${selectedMonthNumber}`;
        const dateString = `${monthString}-${dayString}`;

        const personalDate: PersonalDatePresentationItem = {
            date: dateString,
            icon: personalDateIcon,
            text: personalDateText,
        };

        let newDateset = [personalDate];

        if (dateset) {
            newDateset = newDateset.concat(dateset);
        }

        saveDateset(newDateset).then((response) => {
            setDateset(newDateset);
        });
    };

    const removePersonalDate = (personalDate: PersonalDatePresentationItem) => {
        if (dateset) {
            // The date's unique identifier should be the non-duplicable date string.
            const updatedDateset = dateset.filter(
                (existingDate) => existingDate.date !== personalDate.date,
            );

            saveDateset(updatedDateset).then((response) => {
                setDateset(updatedDateset);
            });
        }
    };

    return (
        <>
            {!isAuthenticated && (
                <ControlsInfoBox>
                    {resources["CustomDates_LogInRequired"]}
                    <br />
                    <div className={styles.infoATag}>
                        <a
                            onClick={() => {
                                updateShowLoginModal(true);
                            }}>
                            {resources["CustomDates_LogIn_or_Register"]}
                        </a>
                    </div>
                </ControlsInfoBox>
            )}
            <ul className={styles.personalDatesUl}>
                {dateset &&
                    dateset.map((dateItem) => (
                        <li
                            className={styles.personalDatesListItem}
                            key={`${dateItem.date} - ${dateItem.text}`}>
                            <div className={styles.personalDatesMedia}>
                                <i className={`fas ${dateItem.icon}`} />
                            </div>
                            <div className={styles.personalDatesText}>
                                {dateItem.text}
                                <span>{dateItem.date}</span>
                            </div>
                            <button
                                className={styles.closeButton}
                                onClick={() => {
                                    removePersonalDate(dateItem);
                                }}>
                                <i className={"fas fa-times"} />
                            </button>
                        </li>
                    ))}
            </ul>
            <div className={styles.inputForm}>
                <div className={styles.dateSelectorDiv}>
                    <div
                        className={[
                            isAuthenticated ? styles.selectIsActive : "",
                            styles.col50,
                        ].join(" ")}>
                        <Select
                            isDisabled={!isAuthenticated}
                            isSearchable={false}
                            value={selectedMonth}
                            options={getMonthOptions()}
                            onChange={(selectedValue) => {
                                const option = selectedValue as OptionType;
                                const selectedMonth = option.value;

                                if (selectedMonth) {
                                    setSelectedMonthNumber(selectedMonth);
                                } else {
                                    setSelectedMonthNumber(null);
                                }

                                setSelectedDayNumber(null);

                                if (errorText !== "") {
                                    validateFields();
                                }
                            }}
                            placeholder={resources["CustomDates_Month"]}
                        />
                    </div>
                    <div
                        className={[
                            isAuthenticated ? styles.selectIsActive : "",
                            styles.col50,
                        ].join(" ")}>
                        <Select
                            isDisabled={!isAuthenticated}
                            isSearchable={false}
                            options={getDayOptions(selectedMonthNumber)}
                            value={selectedDay}
                            onChange={(selectedValue) => {
                                const option = selectedValue as OptionType;
                                const selectedDay = option.value;

                                if (selectedDay) {
                                    setSelectedDayNumber(selectedDay);
                                } else {
                                    setSelectedDayNumber(null);
                                }

                                if (errorText !== "") {
                                    validateFields();
                                }
                            }}
                            placeholder={resources["CustomDates_Day"]}
                        />
                    </div>
                </div>
                <label>{resources["CustomDates_Description"]}</label>
                <input
                    disabled={!isAuthenticated}
                    placeholder={resources["CustomDates_Description_Limit"]}
                    className={styles.inputFormInput}
                    value={personalDateText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.value.length <= 30) {
                            //Remove emojis from the string
                            const strippedText = removeEmojis(e.target.value);
                            setPersonalDateText(strippedText);

                            if (errorText !== "") {
                                validateFields();
                            }
                        }
                    }}
                />
                <label>{resources["CustomDates_Icon"]}</label>
                <ul className={styles.iconPickerUl}>
                    {selectableIcons.map((icon, index) => (
                        <li key={icon + index}>
                            <div
                                onClick={() => {
                                    if (!isAuthenticated) {
                                        return;
                                    }

                                    setPersonalDateIcon(icon);

                                    if (errorText !== "") {
                                        validateFields();
                                    }
                                }}
                                className={[
                                    styles.iconPickerUlDiv,
                                    personalDateIcon === icon
                                        ? styles.iconPickerSelectedDiv
                                        : "",
                                    !isAuthenticated
                                        ? styles.iconPickerUlDivDisabled
                                        : "",
                                ].join(" ")}>
                                <i className={`fas ${icon}`} />
                            </div>
                        </li>
                    ))}
                </ul>
                {errorText !== "" && (
                    <p className={styles.errorText}>{errorText}</p>
                )}
                <Button
                    disabled={!isAuthenticated}
                    onClick={savePersonalDate}
                    text={resources["CustomDates_Button_Add"]}
                    iconClass={"fa fa-plus"}
                    width={"100%"}
                />
            </div>
        </>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: getIsAuthenticated(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
    ...userActionCreators,
    updatePersonalDateInfo,
};

const connectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps,
)(DatesetPicker);

export default connectedComponent;
